import React, { useState, useMemo } from "react";
import NumericPadModal from "./NumericPadModal";
import { computeTicketTotals } from "./ticketCalculations";

const formatPrice = (value) => `${Number(value || 0).toFixed(2)} EUR`;

const CashierTicket = ({ 
  cart, 
  updateQuantity, 
  removeItem, 
  clearCart, 
  total, 
  costMap, 
  taxRates, 
  clientName, 
  orderMode, 
  setOrderMode,
  onCheckout, 
  onQuickAdd,
  pendingQuickProducts,
  onDismissQuickProduct,
  appliedCoupon,
  couponError,
  onApplyCoupon,
  onRemoveCoupon,
  originalTotal,
}) => {
  const [activeNumpadItem, setActiveNumpadItem] = useState(null);
  const [couponInput, setCouponInput] = useState("");

  const tabs = [
    { id: "magasin", label: "EN MAGASIN" },
    { id: "emporter", label: "A EMPORTER" },
    { id: "livrer", label: "A LIVRER" }
  ];

  const totals = useMemo(
    () => computeTicketTotals(cart.items, costMap, taxRates),
    [cart.items, costMap, taxRates]
  );



  const lineCostLabel = (item) => {
    const entry = item.product && item.product.id ? costMap.get(item.product.id) : null;
    const unitCost = entry ? entry.unitCost : (item.product?.purchasePrice || 0);
    return `PA ${Number(unitCost).toFixed(2)}${item.isWeighted ? "/kg" : "/u"}`;
  };

  return (
    <div className="card shadow-sm border-0 h-100 d-flex flex-column" style={{ borderRadius: 0 }}>
      {clientName && (
        <div className="pos-ticket-client">
          <i className="bi bi-person-fill"></i> {clientName}
        </div>
      )}
      {/* Tabs */}
      <div className="d-flex p-2 gap-1 border-bottom" style={{ borderColor: "var(--pos-line)" }}>
        {tabs.map((tab) => (
          <button
            key={tab.id}
            className={`pos-tab ${orderMode === tab.id ? "is-active" : ""}`}
            onClick={() => setOrderMode(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Ticket Items */}
      <div className="flex-grow-1 overflow-auto p-2">
        {cart.items.length === 0 ? (
          <div className="d-flex align-items-center justify-content-center h-100 pos-empty-ticket">
            Panier vide
          </div>
        ) : (
          <ul className="list-unstyled d-flex flex-column gap-2 mb-0">
            {cart.items.map((item) => (
              <li className="pos-ticket-item shadow-sm d-flex flex-column gap-2 p-2" key={item.id}>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={item.product?.imageUrl || "/placeholder.png"}
                      alt={item.title}
                      style={{ width: "30px", height: "30px", objectFit: "cover", borderRadius: "4px" }}
                      onError={(e) => { e.target.src = "https://via.placeholder.com/30"; }}
                    />
                    <span className="fw-bold text-truncate" style={{ maxWidth: "160px", fontSize: "0.9rem" }}>{item.title}</span>
                    {item.isReturn && <span className="badge bg-danger ms-1" style={{ fontSize: "0.65rem" }}>RETOUR</span>}
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <button className="btn btn-sm btn-link text-danger p-0" onClick={() => removeItem(item.id)}>
                      <i className="bi bi-trash fs-5"></i>
                    </button>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-end mt-1">
                  <div className="d-flex align-items-center gap-2">
                    <span className="pos-badge-qty">x {item.quantity}</span>
                    <button
                      className="pos-edit-btn"
                      onClick={() => setActiveNumpadItem(item)}
                    >
                      <i className="bi bi-pencil-square"></i> Qte/Poids
                    </button>
                  </div>
                  <div className="text-end">
                    <div className="fw-bold">{formatPrice(item.lineTotal)}</div>
                    <div style={{ fontSize: "0.75rem", color: "#f97316", fontWeight: "bold" }}>{lineCostLabel(item)}</div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Footer / Totals */}
      <div className="pos-ticket-footer p-3 mt-auto">
        {pendingQuickProducts && pendingQuickProducts.length > 0 && (
          <div className="mb-2 p-2 rounded-2 d-flex align-items-start gap-2" style={{ backgroundColor: "#fffbeb", borderLeft: "3px solid #f59e0b", fontSize: "0.8rem", color: "#92400e" }}>
            <i className="bi bi-info-circle-fill mt-1" style={{ color: "#f59e0b", flexShrink: 0 }}></i>
            <div className="flex-grow-1">
              <div className="fw-bold mb-1">À compléter :</div>
              {pendingQuickProducts.map((p) => (
                <div key={p.id} className="d-flex justify-content-between align-items-center">
                  <span className="text-truncate" style={{ maxWidth: "180px" }}>{p.name}</span>
                  <button className="btn btn-sm p-0 text-decoration-underline" style={{ color: "#92400e", fontSize: "0.75rem" }} onClick={() => onDismissQuickProduct(p.id)}>
                    OK
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Coupon Input */}
        <div className="mb-2">
          {appliedCoupon ? (
            <div className="d-flex align-items-center justify-content-between p-2 rounded-2" style={{ backgroundColor: "#d1fae5", border: "1px solid #6ee7b7", fontSize: "0.8rem" }}>
              <div>
                <i className="bi bi-tag-fill me-1" style={{ color: "#16a34a" }}></i>
                <span className="fw-bold" style={{ color: "#065f46" }}>{appliedCoupon.code}</span>
                <span className="ms-1" style={{ color: "#16a34a" }}>-{appliedCoupon.discount.toFixed(2)} €</span>
              </div>
              <button className="btn btn-sm p-0" style={{ color: "#ef4444" }} onClick={onRemoveCoupon}>
                <i className="bi bi-x-circle-fill"></i>
              </button>
            </div>
          ) : (
            <div className="d-flex gap-1">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Code réduction"
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    onApplyCoupon(couponInput);
                    setCouponInput("");
                  }
                }}
                style={{ fontSize: "0.8rem", borderRadius: "8px" }}
              />
              <button
                className="btn btn-sm fw-bold"
                style={{ backgroundColor: "#dbeafe", color: "#1e40af", borderRadius: "8px", fontSize: "0.8rem" }}
                onClick={() => {
                  onApplyCoupon(couponInput);
                  setCouponInput("");
                }}
              >
                OK
              </button>
            </div>
          )}
          {couponError && (
            <div className="mt-1" style={{ fontSize: "0.7rem", color: "#ef4444" }}>{couponError}</div>
          )}
        </div>
        <div className="d-flex justify-content-between align-items-center mb-2 px-1" style={{ fontSize: "0.85rem", color: "#9ca3af" }}>
          <span>Produits: {totals.count}</span>
          <span>Prix d'achat: {formatPrice(totals.purchaseTotal || 0)}</span>
          <span>Marge: {totals.margin === null ? "0.00 €" : `${totals.margin >= 0 ? "+" : ""}${totals.margin.toFixed(2)} €`}</span>
        </div>
        {appliedCoupon && (
          <div className="d-flex justify-content-between align-items-center mb-1 px-1" style={{ fontSize: "0.8rem", color: "#16a34a" }}>
            <span><i className="bi bi-tag-fill me-1"></i>Réduction ({appliedCoupon.code})</span>
            <span className="fw-bold">-{formatPrice(appliedCoupon.discount)}</span>
          </div>
        )}
        <div className="d-flex gap-2">
          <button className="pos-clear-btn d-flex justify-content-center align-items-center" onClick={clearCart} aria-label="Vider le ticket">
            <i className="bi bi-trash-fill"></i>
          </button>
          <button className="pos-pay-btn" onClick={onCheckout}>
            <span>REGLER</span>
            <span>{formatPrice(total)}</span>
          </button>
          <button
            className="d-flex justify-content-center align-items-center fw-bold"
            style={{ width: "48px", height: "48px", borderRadius: "8px", backgroundColor: "#22c55e", color: "white", fontSize: "1.5rem", flexShrink: 0 }}
            onClick={onQuickAdd}
            title="Ajout rapide"
          >
            +
          </button>
        </div>
      </div>

      {activeNumpadItem && (
        <NumericPadModal
          isOpen={true}
          title={activeNumpadItem.title}
          unit={activeNumpadItem.isWeighted ? "kg" : "qte"}
          initialValue={activeNumpadItem.quantity}
          onClose={() => setActiveNumpadItem(null)}
          onValidate={(val) => {
            updateQuantity(activeNumpadItem.id, val - activeNumpadItem.quantity, activeNumpadItem.quantity);
            setActiveNumpadItem(null);
          }}
        />
      )}
    </div>
  );
};

export default CashierTicket;
